import cn from "classnames";
import BookIndex from "components/shared/book/list";
import SharedReactTable from "components/shared/reactTable";
import { dateTimeToView } from "helpers/date";
import { fuelCardTypes } from "helpers/enums";
import { paths } from "helpers/paths";
import { DateColumnFilter, EnumColumnFilter, rowIndexColumn, SelectColumnFilter } from "helpers/reactTable";
import { enumLabel } from "helpers/system";
import Tooltip from "rc-tooltip";
import React from "react";
import { Link } from "react-router-dom";
import { list as drivers } from "sources/books/drivers";
import { getAllListExcel, getCarListExcel, getDriverListExcel, getUnlinkedListExcel } from "sources/books/fuelCards";
import { list as trucks } from "sources/books/trucks";
import withContainer from "./container";

class FuelCards extends BookIndex {
    get listProps() {
        const { tab } = this.props;
        const commonColumns = [
            { Header: "ID", accessor: "id", width: 100, filterable: true, filterType: "int" },
            {
                Header: "Номер карты",
                accessor: "number",
                filterable: true,
                Cell: ({ value, row }) =>
                    row.original?.hasNoLimitManagement ? (
                        <span className="text-muted">
                            <Tooltip overlay="Без управления лимитами" placement="bottom">
                                <i className="icon-uEA04-cancel inline-icon pr-1" />
                            </Tooltip>
                            {value}
                        </span>
                    ) : (
                        value
                    ),
            },
            {
                Header: "Тип карты",
                accessor: "type",
                filterable: true,
                filterType: "enum",
                Filter: (props) => <EnumColumnFilter {...props} filterEnum={fuelCardTypes} />,
                Cell: ({ value }) => enumLabel(fuelCardTypes, value),
            },
            {
                Header: "Дата создания",
                accessor: "createdAt",
                filterable: true,
                filterType: "date",
                sortable: true,
                Filter: DateColumnFilter,
                Cell: ({ value }) => dateTimeToView(value),
                CSVExport: ({ value }) => dateTimeToView(value),
            },
            {
                Header: "Дата редактирования",
                accessor: "updatedAt",
                filterable: true,
                filterType: "date",
                sortable: true,
                Filter: DateColumnFilter,
                Cell: ({ value }) => dateTimeToView(value),
                CSVExport: ({ value }) => dateTimeToView(value),
            },
            {
                Header: "",
                accessor: "actions",
                sortable: false,
                notExportable: true,
                Cell: ({ row }) => {
                    if (row.isGrouped) {
                        return null;
                    }
                    return (
                        <Link className="btn btn-xs btn-outline-primary" to={paths.fuelCardsShow(row.original.id)}>
                            Изменить
                        </Link>
                    );
                },
            },
        ];
        switch (tab) {
            case "all":
                return {
                    getList: this.props.getAllFuelCards,
                    source: { getListExcel: getAllListExcel },
                    items: this.props.allFuelCards,
                    count: this.props.allItemsCount,
                    columns: [
                        rowIndexColumn,
                        {
                            Header: "Водитель, машина",
                            accessor: "car",
                            filterable: false,
                            sortable: false,
                            Cell: ({ row: { original } }) =>
                                original.car ? (
                                    <>
                                        <b>Машина: </b>
                                        {original.car.number}
                                    </>
                                ) : original.driver ? (
                                    <>
                                        <b>Водитель: </b>
                                        {original.driver.fullName}
                                    </>
                                ) : (
                                    <b>Непривязанная карта</b>
                                ),
                        },
                        ...commonColumns,
                    ],
                };
            case "unlinked":
                return {
                    getList: this.props.getUnlinkedFuelCards,
                    source: { getListExcel: getUnlinkedListExcel },
                    items: this.props.unlinkedFuelCards,
                    count: this.props.unlinkedItemsCount,
                    columns: [rowIndexColumn, ...commonColumns],
                };
            case "drivers":
                return {
                    getList: this.props.getDriverFuelCards,
                    source: { getListExcel: getDriverListExcel },
                    items: this.props.driverFuelCards.reduce((acc, item) => {
                        const cards = item.cards.map((card) => ({
                            ...card,
                            driverId: item.driver.id,
                            driverFullName: item.driver.fullName,
                        }));
                        acc.push(...cards);
                        return acc;
                    }, []),
                    count: this.props.driverItemsCount,
                    columns: [
                        {
                            Header: "Водитель",
                            accessor: "driverId",
                            Cell: ({ row: { subRows } }) => subRows[0].original.driverFullName,
                            filterable: true,
                            filterType: "select",
                            Filter: (props) => (
                                <SelectColumnFilter
                                    {...props}
                                    selectProps={{
                                        url: drivers,
                                        inputProps: { name: "driverId" },
                                        filterPropName: "user/fullName",
                                        propName: "user.fullName",
                                    }}
                                />
                            ),
                        },
                        ...commonColumns,
                    ],
                };
            case "cars":
                return {
                    getList: this.props.getCarFuelCards,
                    source: { getListExcel: getCarListExcel },
                    items: this.props.carFuelCards.reduce((acc, item) => {
                        const cards = item.cards.map((card) => ({
                            ...card,
                            carId: item.car?.id,
                            carNumber: item.car?.number,
                        }));
                        acc.push(...cards);
                        return acc;
                    }, []),
                    count: this.props.carItemsCount,
                    columns: [
                        {
                            Header: "Номер машины",
                            accessor: "carId",
                            Cell: ({ row: { subRows } }) => subRows[0].original.carNumber,
                            filterable: true,
                            filterType: "select",
                            Filter: (props) => (
                                <SelectColumnFilter
                                    {...props}
                                    selectProps={{
                                        url: trucks,
                                        inputProps: { name: "carId" },
                                        propName: "number",
                                    }}
                                />
                            ),
                        },
                        ...commonColumns,
                    ],
                };
            default:
                return {};
        }
    }

    changeTab = (tab) => {
        this.props.clearParams();
        this.props.history.push(`${paths.fuelCards}/list?tab=${tab}`);
    };

    renderCreateButton = () => {
        const { modal, mergeParams } = this.props;
        const action = () =>
            modal({
                name: "createFuelCard",
                title: "Создание топливной карты",
                windowMod: "wide",
                mergeParams,
            });
        return (
            <a className="btn btn-brand btn-sm" onClick={action}>
                Добавить карту
            </a>
        );
    };

    renderCounter = () => {
        const { params } = this.props;
        const { count } = this.listProps;
        const { start, length } = params;
        return (
            <>
                &nbsp;
                <small>
                    (Отображены&nbsp;
                    <strong>
                        {count === 0 ? 0 : start + 1}-{start + length > count ? count : start + length}
                    </strong>
                    &nbsp;из <strong>{count}</strong>)
                </small>
            </>
        );
    };

    renderListComponent = () => {
        const { tab } = this.props;
        const tableProps = {
            className: "-striped mb-3 table-sm h-100 -highlight",
            ...(tab === "all" ? {} : { initialState: { groupBy: ["carId", "driverId"] } }),
        };
        return (
            <>
                <div className="table-tabs">
                    <div
                        onClick={this.changeTab.bind(this, "all")}
                        className={cn("table-tabs__item", {
                            "table-tabs__item--active": tab === "all",
                        })}
                    >
                        Общий список
                    </div>
                    <div
                        onClick={this.changeTab.bind(this, "cars")}
                        className={cn("table-tabs__item", {
                            "table-tabs__item--active": tab === "cars",
                        })}
                    >
                        Машины
                    </div>
                    <div
                        onClick={this.changeTab.bind(this, "drivers")}
                        className={cn("table-tabs__item", {
                            "table-tabs__item--active": tab === "drivers",
                        })}
                    >
                        Водители
                    </div>
                    <div
                        onClick={this.changeTab.bind(this, "unlinked")}
                        className={cn("table-tabs__item", {
                            "table-tabs__item--active": tab === "unlinked",
                        })}
                    >
                        Непривязанные карты
                    </div>
                </div>
                <SharedReactTable key={tab} {...this.props} {...this.listProps} tableProps={tableProps} />
            </>
        );
    };
}

export default withContainer(FuelCards, "fuel-cards");
